import { useState } from "react";
import { ShieldCheck, Copy, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useLoad, act } from "@/components/common";

function BackupCodes({ data, reload }) {
  const [pw, setPw] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      setCodes((await api.post("/auth/backup-codes", { password: pw })).data.codes);
      setPw("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const text = codes && `Aspire backup sign-in codes\nEach code works once.\n\n${codes.join("\n")}\n`;
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    a.download = "aspire-backup-codes.txt";
    a.click();
  };
  return (
    <section className="space-y-3 rounded-lg border p-4" data-testid="backup-codes-section">
      <div className="font-medium">Backup sign-in codes</div>
      <p className="text-sm text-slate-500" data-testid="backup-codes-status">
        {data.backup_total ? `${data.backup_left} of ${data.backup_total} unused. Use one when you can't get the emailed code.` : "You haven't created backup codes yet. Create them so you can still sign in if email is unavailable."}
      </p>
      {codes ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 rounded-md bg-slate-900 p-4 font-mono text-sm text-white" data-testid="backup-codes-list">{codes.map((c) => <span key={c}>{c}</span>)}</div>
          <p className="text-xs text-amber-700">Save these now. You won't be able to see them again, and the old ones no longer work.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid="backup-codes-copy" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Copied"))}><Copy className="mr-1.5 h-4 w-4" />Copy</Button>
            <Button size="sm" variant="outline" data-testid="backup-codes-download" onClick={download}><Download className="mr-1.5 h-4 w-4" />Download .txt</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input type="password" placeholder="Confirm your password" data-testid="backup-codes-password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <Button data-testid="backup-codes-generate" disabled={!pw || busy} onClick={generate}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{data.backup_total ? "Replace codes" : "Create codes"}</Button>
        </div>
      )}
    </section>
  );
}

function Devices({ data, reload }) {
  const forget = (device_id) => act(api.post("/auth/trusted-devices/forget", { device_id }), device_id ? "Device forgotten" : "All devices forgotten").then(reload);
  return (
    <section className="space-y-3 rounded-lg border p-4" data-testid="trusted-devices-section">
      <div className="flex items-center justify-between">
        <div className="font-medium">Remembered devices</div>
        {data.trusted_devices.length > 0 && <Button size="sm" variant="outline" data-testid="forget-all-devices" onClick={() => forget(null)}>Forget all</Button>}
      </div>
      {data.trusted_devices.length === 0 && <p className="text-sm text-slate-500" data-testid="no-trusted-devices">No devices are remembered. Tick "Remember this device" when you enter a code.</p>}
      {data.trusted_devices.map((d) => (
        <div key={d.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`trusted-device-${d.id}`}>
          <div className="min-w-0"><div className="truncate text-slate-700">{d.label}</div><div className="text-xs text-slate-400">Added {new Date(d.created_at).toLocaleDateString()} · expires {new Date(d.expires_at).toLocaleDateString()}</div></div>
          <button className="shrink-0 text-xs text-red-600 hover:underline" data-testid={`forget-device-${d.id}`} onClick={() => forget(d.id)}>Forget</button>
        </div>
      ))}
    </section>
  );
}

function Body() {
  const { data, reload } = useLoad(() => api.get("/auth/security").then((r) => r.data));
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />;
  return <div className="min-w-0 space-y-4"><BackupCodes data={data} reload={reload} /><Devices data={data} reload={reload} /></div>;
}

export function SecurityPanel() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="security-btn" onClick={() => setOpen(true)} className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100" title="Sign-in security"><ShieldCheck className="h-4 w-4" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="security-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Sign-in security</DialogTitle>
            <DialogDescription>Your account uses two-step sign-in with a code sent by email.</DialogDescription>
          </DialogHeader>
          {open && <Body />}
        </DialogContent>
      </Dialog>
    </>
  );
}
